<template>
    <div>
        <div v-if="fixedPrices === 1" class="row">
            <div class="col-md-10 col-md-offset-1">
                <!-- for consistent spacing -->
                <h3></h3>
                <div class="vrm-form__fieldset-item vrm-form__fieldset-item--checkbox">
                    <label class="sg-textfield-label">
                        <span class="sg-textfield-label-caption">
                            {{ $t(`dynamicess.${type}PricesQuestionPricesDiffer`) }}
                        </span>
                    </label>
                    <div
                        class="vrm-form__custom-radio-checkbox vrm-form__custom-radio-checkbox--inline"
                    >
                        <div
                            v-for="optionType in Object.values(YES_NO)"
                            :key="optionType"
                            class="sg-custom-check-and-radio custom-radio "
                            :class="{ active: differentPricesWeek === optionType }"
                            @click="changeDifferentPricesWeek(optionType)"
                        >
                            <label>
                                <input type="radio" :checked="differentPricesWeek === optionType" />
                                <div class="sg-alternative-display">
                                    <div class="sg-outer">
                                        <div class="sg-inner"></div>
                                    </div>
                                </div>
                            </label>
                            <label for="differentPricesWeek">
                                {{ $t('dynamicess.choice_' + optionType) }}
                            </label>
                        </div>
                    </div>
                </div>
                <fixed-schedule
                    v-if="differentPricesWeek !== null"
                    value-field="price"
                    :type-label="$t('ess.price', { i18nFallback: 'Price' })"
                    :with-weekend="differentPricesWeek"
                    :rows-week="rowsWeek"
                    :rows-weekend="rowsWeekend"
                    @rows-updated="updateRows"
                    @error="$emit('error', $event)"
                >
                    <template v-slot:title>
                        <h3>
                            {{
                                $t('ess.fixed_prices_schedule', {
                                    i18nFallback: 'Fixed prices schedule',
                                })
                            }}
                        </h3>
                    </template>
                </fixed-schedule>
            </div>
        </div>

        <div v-if="fixedPrices === 0">
            <br />
            <!-- Country and Provider selection -->
            <div class="row">
                <div class="col-md-10 col-md-offset-1">
                    <div class="row">
                        <h4 id="countrySelect">{{ $t('dynamicess.selectCountry') }}</h4>
                    </div>
                    <br />
                    <div class="row">
                        <div class="col-xs-12 col-sm-4 p-l-0">
                            <fieldset class="vrm-fieldset">
                                <div class="vrm-form__fieldset-item">
                                    <label class="sg-textfield-label">
                                        {{ $t('dynamicess.country') }}
                                    </label>
                                    <vrm-dropdown
                                        v-model="buyCountry"
                                        :remove-selected="true"
                                        :options="countries"
                                        :display-name="displayName"
                                        :disabled="!fixedBuyPrices && type == 'sell'"
                                        :search-enabled="true"
                                        @input="changeCountry"
                                    />
                                </div>
                            </fieldset>
                        </div>
                        <div class="col-xs-12 col-sm-4 p-l-0">
                            <fieldset class="vrm-fieldset">
                                <div class="vrm-form__fieldset-item">
                                    <label class="sg-textfield-label">
                                        {{ $t('dynamicess.provider') }}
                                    </label>
                                    <vrm-dropdown
                                        :value="provider"
                                        :disabled="country == null"
                                        :remove-selected="true"
                                        :options="providers"
                                        :search-enabled="true"
                                        @input="changeProvider"
                                    />
                                </div>
                            </fieldset>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="provider && provider.id == -1" class="row">
                <div class="col-xs-12 col-sm-6 col-md-offset-1 p-l-0">
                    <vrm-text-field
                        :model="providerName"
                        :required="true"
                        :title="
                            $t('dynamicess.providerName', {
                                i18nFallback: 'Provider Name',
                            })
                        "
                        @input="changeProviderName"
                    ></vrm-text-field>
                </div>
            </div>
            <!-- France EDF - Tempo specific settings -->
            <EdfTempoForm
                v-if="isFranceAndEDFProviderSelected"
                :tempo-overwrite-blue-day-hours="tempoOverwriteBlueDayHours"
                :tempo-overwrite-blue-night-hours="tempoOverwriteBlueNightHours"
                :tempo-overwrite-red-day-hours="tempoOverwriteRedDayHours"
                :tempo-overwrite-red-night-hours="tempoOverwriteRedNightHours"
                :tempo-overwrite-white-day-hours="tempoOverwriteWhiteDayHours"
                :tempo-overwrite-white-night-hours="tempoOverwriteWhiteNightHours"
                @on-change-tempo-overwrite-blue-day-hours="changeTempoOverwriteBlueDayHours"
                @on-change-tempo-overwrite-blue-night-hours="changeTempoOverwriteBlueNightHours"
                @on-change-tempo-overwrite-red-day-hours="changeTempoOverwriteRedDayHours"
                @on-change-tempo-overwrite-red-night-hours="changeTempoOverwriteRedNightHours"
                @on-change-tempo-overwrite-white-day-hours="changeTempoOverwriteWhiteDayHours"
                @on-change-tempo-overwrite-white-night-hours="changeTempoOverwriteWhiteNightHours"
            />

            <!-- UK Specific Octopus settings -->
            <div v-if="isOctopusUKProviderSelected" class="row">
                <div class="col-md-10 col-md-offset-1">
                    <div class="row">
                        <div class="col-xs-12 col-sm-12 col-md-12 p-l-0">
                            <h2>
                                {{
                                    $t('dynamicess.headerAccountDetailsOctopus', {
                                        i18nFallback: 'Octopus Account Details',
                                    })
                                }}
                            </h2>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-xs-12 col-sm-4 p-l-0 vrm-wizard__octopus-input">
                            <vrm-text-field
                                :model="accountNumber"
                                :required="true"
                                :title="
                                    $t('dynamicess.labelAccountNumber', {
                                        i18nFallback: 'Account Number',
                                    })
                                "
                                :tooltip="
                                    $t('dynamicess.tooltipAccountNumber', {
                                        i18nFallback: `You can find this on a bill or any e-mail from Octopus.`,
                                    })
                                "
                                @input="changeAccountNumber"
                            ></vrm-text-field>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-xs-12 col-sm-4 p-l-0">
                            <div class="vrm-form__fieldset-item vrm-wizard__debit-checkbox">
                                <div class="vrm-checkbox__wrapper__with-label">
                                    <vrm-checkbox
                                        input-id="directDebit"
                                        :model="directDebit"
                                        @input="changeDirectDebit"
                                    ></vrm-checkbox>

                                    <label for="directDebit">
                                        {{
                                            $t('dynamicess.labelDirectDebit', {
                                                i18nFallback: 'I am using direct debit',
                                            })
                                        }}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-xs-12 col-sm-12 p-l-0">
                            <h4>
                                {{
                                    $t('dynamicess.headerApiKey', {
                                        i18nFallback: 'API Key',
                                    })
                                }}
                            </h4>

                            <div
                                class="vrm-dess-wizard__intro"
                                v-html="
                                    $t('dynamicess.introApiKey', {
                                        i18nFallback: `To access your energy prices, your personal API key provided by Octopus is required. This API key can be found in your account information. Detailed instructions on where to find your API key can be found in this video: <a href='https://www.youtube.com/watch?v=lEfry72-vdQ' target='_blank'>https://www.youtube.com/watch?v=lEfry72-vdQ</a>`,
                                    })
                                "
                            ></div>
                        </div>

                        <div class="col-xs-12 col-sm-6 p-l-0">
                            <vrm-text-field
                                :model="apiKey"
                                :required="true"
                                :title="
                                    $t('dynamicess.labelApiKey', {
                                        i18nFallback: 'Octopus API Key',
                                    })
                                "
                                :tooltip="
                                    $t('dynamicess.tooltipApiKey', {
                                        i18nFallback: `You can find this in your Octopus profile under 'My API keys'.`,
                                    })
                                "
                                @input="changeApiKey"
                            ></vrm-text-field>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="isOctopusUKProviderSelected" class="row">
                <br />
                <div class="col-md-10 col-md-offset-1">
                    <div class="vrm-info-box--flex">
                        <span>
                            {{
                                $t(`dynamicess.toggleVAT`, {
                                    i18nFallback: `My Octopus API price includes VAT`,
                                })
                            }}
                        </span>
                        <vrm-toggle v-model="vat" />
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-md-10 col-md-offset-1">
                    <div class="vrm-form__fieldset-item vrm-form__fieldset-item--checkbox">
                        <label class="sg-textfield-label">
                            <span class="sg-textfield-label-caption">
                                {{ $t('dynamicess.choosePriceSamplingRate') }}
                            </span>
                        </label>
                        <div
                            class="vrm-form__custom-radio-checkbox vrm-form__custom-radio-checkbox--inline"
                        >
                            <div
                                v-for="option in priceSamplingRates"
                                :key="option.value"
                                class="sg-custom-check-and-radio custom-radio"
                                :class="{ active: priceSamplingRate === option.value }"
                                @click="changePriceSamplingRate(option.value)"
                            >
                                <label>
                                    <input
                                        type="radio"
                                        :checked="priceSamplingRate === option.value"
                                    />
                                    <div class="sg-alternative-display">
                                        <div class="sg-outer">
                                            <div class="sg-inner"></div>
                                        </div>
                                    </div>
                                </label>
                                <label for="priceSamplingRate">
                                    {{
                                        $t(option.labelKey, { i18nFallback: option.labelFallback })
                                    }}
                                </label>
                            </div>
                            <template v-if="priceSamplingRate === 15">
                                <div
                                    class="sg-vrm-icon_012_alarm-32px mb-6 p-6 sg-color-orange sg-textfield-label text-sm"
                                    role="alert"
                                >
                                    <span class="text-sm">
                                        {{
                                            $t(`dynamicess.warning15MinSupport`, {
                                                i18nFallback:
                                                    'Please make sure your energy contract supports 15-minute price intervals. If DESS stops working, it may be due to lack of support from your provider.',
                                            })
                                        }}
                                    </span>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Bidding zone selection -->
            <div v-if="showBiddingZones" class="row">
                <div class="col-md-10 col-md-offset-1">
                    <div class="row">
                        <h4>
                            {{
                                $t('dynamicess.selectBiddingZone', {
                                    i18nFallback: `Select your bidding zone`,
                                })
                            }}
                        </h4>
                        <span
                            v-if="showNotSureText"
                            v-html="
                                $t('dynamicess.notSureBiddingZone', {
                                    i18nFallback:
                                        'Are you not sure about your bidding zone? Visit the <a target=\'_blank\' href=\'https://newtransparency.entsoe.eu/\'>ENTSO-E map</a> to find your region.',
                                })
                            "
                        ></span>
                    </div>
                    <div class="row">
                        <div class="col-xs-12 col-sm-4 p-l-0">
                            <fieldset class="vrm-fieldset">
                                <div class="vrm-form__fieldset-item">
                                    <label class="sg-textfield-label">
                                        {{ $t('dynamicess.biddingZone') }}
                                    </label>
                                    <!-- Removing spain from being a possible bidding zone as mentioned in issue #2028 -->
                                    <vrm-dropdown
                                        v-model="biddingZone"
                                        :remove-selected="true"
                                        :options="validBiddingZones"
                                        :disabled="type == 'sell' && !fixedBuyPrices"
                                        @input="changeBiddingZone"
                                    />
                                </div>
                            </fieldset>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Formula builder -->
            <!-- Hide the formula builder if one of the providers that don't need formulas is selected (ex: Octopus for UK) -->
            <div v-if="!isProviderSelected" class="row">
                <div class="col-md-10 col-md-offset-1">
                    <vrm-formula-builder
                        :model="priceFormula"
                        :required="true"
                        :disabled="provider == null"
                        :title="$t(`dynamicess.${type}Formula`)"
                        @input="changePriceFormula"
                        @error="hasError => $emit('error', { formula: hasError })"
                    ></vrm-formula-builder>
                </div>
            </div>
            <div
                v-if="!isFranceAndEDFProviderSelected && !isVarioProviderSelected"
                id="toggle"
                class="row"
            >
                <br />
                <div class="col-md-10 col-md-offset-1">
                    <div class="vrm-info-box--flex">
                        <span
                            v-html="
                                $t(`dynamicess.toggleNegativePriceClamping`, {
                                    i18nFallback: `Does your energy provider support negative prices? Otherwise negative prices will be considered the same as 0.`,
                                })
                            "
                        ></span>
                        <vrm-toggle v-model="toggleNegPriceClamping" />
                    </div>
                </div>
            </div>
        </div>
        <br />
    </div>
</template>
<script>
import { Vue, Component, Prop } from 'vue-property-decorator';
import VrmTextField from '@/vue/components/forms/VrmTextField.vue';
import SelectorPrices from '@/vue/components/pages/dynamic-ess/forms/SelectorPrices.vue';
import InputError from '@/vue/components/forms/InputError.vue';
import FixedSchedule from '@/vue/components/pages/dynamic-ess/forms/FixedSchedule.vue';
import VrmDropdown from '@/vue/components/shared/VrmDropdown';
import EdfTempoForm from '@/vue/components/pages/dynamic-ess/forms/EdfTempoForm.vue';
import VrmFormulaBuilder from '@/vue/components/pages/dynamic-ess/forms/VrmFormulaBuilder.vue';
import VrmToggle from '@/vue/components/shared/toggles/VrmToggle';
import {
    DYNAMIC_ESS,
    DYNAMIC_ESS_FORM_FIELDS,
    countriesTranslations,
    idCountryUnitedKingdom,
    idProviderOctopus,
    ID_COUNTRY_SPAIN,
    ID_BIDDING_ZONE_SPAIN,
    ID_COUNTRY_FRANCE,
    ID_PROVIDER_EDF_TEMPO,
    ID_PROVIDERS_VARIO,
    PRICE_SAMPLING_RATES,
} from '@/vue/store/modules/dynamic-ess-form.js';
import VrmCheckbox from '@/vue/components/forms/VrmCheckbox.vue';
import { vrmConfig } from '@/config';

@Component({
    components: {
        VrmCheckbox,
        VrmTextField,
        SelectorPrices,
        InputError,
        FixedSchedule,
        VrmDropdown,
        VrmFormulaBuilder,
        VrmToggle,
        EdfTempoForm,
    },
})
export default class GenericPrices extends Vue {
    YES_NO = {
        YES: 1,
        NO: 0,
    };
    @Prop()
    fixedPrices;
    @Prop()
    differentPricesWeek;
    @Prop()
    type;
    @Prop()
    priceSamplingRate;

    @Prop()
    directDebit;

    @Prop()
    accountNumber;

    @Prop()
    apiKey;

    @Prop()
    country;

    changeDifferentPricesWeek(value) {
        this.$emit('update:different-prices-week', value);
    }

    get priceSamplingRates() {
        return PRICE_SAMPLING_RATES;
    }

    changePriceSamplingRate(value) {
        this.$emit('update:price-sampling-rate', value);
    }

    @Prop()
    rowsWeek;
    @Prop()
    rowsWeekend;
    updateRows(value) {
        this.$emit('rows-updated', value);
    }

    @Prop()
    priceFormula;
    changePriceFormula(value) {
        this.$emit('update:price-formula', value);
    }

    @Prop()
    provider;
    changeProvider(value) {
        this.$emit('update:provider', value);
    }

    @Prop()
    providerName;
    changeProviderName(value) {
        this.$emit('update:provider-name', value);
    }

    @Prop({ type: String, default: false })
    toggleNegPriceClampingProperty;

    @Prop()
    tempoOverwriteBlueDayHours;
    @Prop()
    tempoOverwriteBlueNightHours;
    @Prop()
    tempoOverwriteRedDayHours;
    @Prop()
    tempoOverwriteRedNightHours;
    @Prop()
    tempoOverwriteWhiteDayHours;
    @Prop()
    tempoOverwriteWhiteNightHours;

    get toggleNegPriceClamping() {
        return !this.$store.state.dynamicEssForm[this.toggleNegPriceClampingProperty];
    }

    set toggleNegPriceClamping(value) {
        value = !value;
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value,
            key: DYNAMIC_ESS_FORM_FIELDS[this.toggleNegPriceClampingProperty],
        });
    }

    get vat() {
        return this.$store.state.dynamicEssForm.vat;
    }

    set vat(value) {
        this.$emit('update:vat', value);
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value,
            key: DYNAMIC_ESS_FORM_FIELDS.vat,
        });
    }

    get fixedBuyPrices() {
        return this.$store.state.dynamicEssForm.fixedBuyPrices;
    }

    displayName(value) {
        return this.$t(countriesTranslations(value), { i18nFallback: `${value}` });
    }

    get countries() {
        return this.$store.state.dynamicEssForm.countries;
    }

    get biddingZones() {
        return this.$store.getters[DYNAMIC_ESS.BIDDING_ZONES];
    }

    get biddingZone() {
        return this.$store.state.dynamicEssForm.biddingZone;
    }

    set biddingZone(value) {
        this.$store.commit(DYNAMIC_ESS.SET_FORM_FIELD, {
            value,
            key: DYNAMIC_ESS_FORM_FIELDS.biddingZone,
        });
    }

    get showBiddingZones() {
        return (
            this.biddingZones &&
            this.biddingZones.length > 1 &&
            !(this.isSpainSelected && this.provider.id == -1)
        );
    }

    get providers() {
        if (this.type === 'sell' || !vrmConfig.EdfTempoEnabled || !vrmConfig.VarioEnabled) {
            return [
                { id: -1, name: this.$t('dynamicess.notInTheList') },
                ...this.$store.getters[DYNAMIC_ESS.PROVIDERS].filter(
                    provider =>
                        parseInt(provider.idProvider) !== ID_PROVIDER_EDF_TEMPO &&
                        !ID_PROVIDERS_VARIO.includes(parseInt(provider.idProvider)),
                ),
            ];
        } else {
            return [
                { id: -1, name: this.$t('dynamicess.notInTheList') },
                ...this.$store.getters[DYNAMIC_ESS.PROVIDERS],
            ];
        }
    }

    get isOctopusUKProviderSelected() {
        return this.isUnitedKingdomSelected && this.isOctopusProviderSelected;
    }
    get isUnitedKingdomSelected() {
        return this.country && this.country.id == idCountryUnitedKingdom;
    }

    get isOctopusProviderSelected() {
        return this.provider && this.provider.idProvider == idProviderOctopus;
    }

    get isSpainSelected() {
        return this.country && this.country.id == ID_COUNTRY_SPAIN;
    }
    get showNotSureText() {
        return !this.isSpainSelected;
    }

    //Spain is not a valid bidding zone! #2028
    get validBiddingZones() {
        return this.biddingZones.filter(
            zone => zone.idZone !== ID_BIDDING_ZONE_SPAIN && zone.name !== 'Spain',
        );
    }

    get isFranceSelected() {
        return this.country && this.country.id == ID_COUNTRY_FRANCE;
    }

    get isFranceAndEDFProviderSelected() {
        return (
            this.country &&
            this.country.id == ID_COUNTRY_FRANCE &&
            this.provider &&
            this.provider.idProvider == ID_PROVIDER_EDF_TEMPO
        );
    }

    get isVarioProviderSelected() {
        return this.provider && ID_PROVIDERS_VARIO.includes(this.provider.idProvider);
    }

    get isProviderSelected() {
        return (
            this.isOctopusProviderSelected ||
            this.isFranceAndEDFProviderSelected ||
            this.isVarioProviderSelected
        );
    }

    get buyCountry() {
        return this.country;
    }

    set buyCountry(value) {
        this.$emit('update:country', value);
    }

    changeAccountNumber(value) {
        this.$emit('update:account-number', value);
    }

    changeDirectDebit(value) {
        this.$emit('update:direct-debit', value);
    }

    changeApiKey(value) {
        this.$emit('update:api-key', value);
    }

    changeCountry(value) {
        this.$emit('update:country', value);
    }
    changeBiddingZone(value) {
        this.$emit('update:bidding-zone', value);
    }

    changeTempoOverwriteBlueDayHours(value) {
        this.$emit('update:tempo-overwrite-blue-day-hours', value);
    }

    changeTempoOverwriteBlueNightHours(value) {
        this.$emit('update:tempo-overwrite-blue-night-hours', value);
    }

    changeTempoOverwriteRedDayHours(value) {
        this.$emit('update:tempo-overwrite-red-day-hours', value);
    }

    changeTempoOverwriteRedNightHours(value) {
        this.$emit('update:tempo-overwrite-red-night-hours', value);
    }

    changeTempoOverwriteWhiteDayHours(value) {
        this.$emit('update:tempo-overwrite-white-day-hours', value);
    }

    changeTempoOverwriteWhiteNightHours(value) {
        this.$emit('update:tempo-overwrite-white-night-hours', value);
    }
}
</script>
